import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Box, Button, Chip, Typography } from '@mui/material'
import PlanService from '../../services/PlanService'
import TipContext from '../../providers/GlobalTip'
import WeeklyPlanningGrid from './WeeklyPlanningGrid'
import PlanEditMonthlyHeader from './PlanEditMonthlyHeader'
import { hasExplicitPlanInventoryOverrideForMonth, hasMeaningfulMonthlyPlanValues, shouldUseDailyMonthValues } from './PlanEditMonthlyGridShell'
import { makePlannerDraftKey, clearPlannerDraft } from '../../lib/PlannerDraftStore'
import { rollupMonthlyToWeekly } from '../../lib/PlannerBidirSync'
import { rollupWeeklyToMonthly } from '../../lib/PlannerRollup'
import { applyEditAllocateMonthLocked, applyEditAggregate, rollupDailyToMonths, rollupDailyToWeeks } from '../../lib/PlannerDailyModel'
import { seedWeeklyPlansFromMonthlyBase, getDominantEpochMonthForWeek } from '../../lib/PlannerWeeklySeed'
import {
  deriveWeeklyMonthPercentDisplay,
  deriveWeeklyPlanPercentDisplay,
  deriveWeeklySalesTotalFromPlanPercent,
  deriveWeeklyTotalFromMonthPercent,
} from '../../lib/PlannerWeeklyPercent'
import { allocateMonthlyMarkdownActualsToWeeks, allocateMonthlySalesActualsToWeeks, allocateMonthlyInflowActualsToWeeks } from '../../lib/PlannerWeeklyActuals'
import { buildPlannerAnnualValues } from '../../lib/PlannerAnnualValues'
import { buildWeeklySummary, formatMonthLabelWithSaturdayComparison, formatRepresentativeMonthLabel, formatWeekStartLabel } from '../../lib/PlannerWeeklyView'
import { deriveMonthlyInflows } from '../../lib/PlannerRollup'

export const buildEffectiveMonthlyPlanRows = ({ plannerSession, calcMode = 'allocate' }) => {
  const monthlyRows = Array.isArray(plannerSession?.monthlyBaseUi) ? plannerSession.monthlyBaseUi : []
  const monthlyActualRows = Array.isArray(plannerSession?.monthlyBase) ? plannerSession.monthlyBase : []
  const monthlyEditsByMonth = plannerSession?.monthlyEditsByMonth || {}
  const weeklyEditsByWeek = plannerSession?.weeklyEditsByWeek || {}
  const weekToMonthDayCounts = plannerSession?.weekToMonthDayCounts || new Map()
  const weeklyActualSalesByWeek = plannerSession?.weeklyActualSalesByWeek || {}

  if (!monthlyRows.length) return []

  const effectiveMonths = rollupWeeklyToMonthly({
    monthlyBase: monthlyRows,
    monthlyEdits: monthlyEditsByMonth,
    weeklyEdits: weeklyEditsByWeek,
    weekToMonthDayCounts,
    weeklyActualSalesByWeek,
    calcMode,
  }).map((m) => ({
    ...m,
    imuPlan: Number(m?.imuPlan ?? m?.planImuPct ?? 0),
  }))

  const hasDaily = !!(
    plannerSession?.dailyByDate &&
    typeof plannerSession?.dailyByDate?.values === 'function' &&
    plannerSession?.datesByMonth &&
    typeof plannerSession?.datesByMonth?.get === 'function'
  )

  if (!hasDaily) return effectiveMonths

  const epochMonths = effectiveMonths.map((row) => String(row?.epochMonth)).filter(Boolean)
  const byMonth = epochMonths.length ? rollupDailyToMonths({ byDate: plannerSession.dailyByDate, epochMonths }) : null

  return effectiveMonths.map((m) => {
    const em = String(m?.epochMonth)
    const dates = plannerSession?.datesByMonth?.get?.(em) || []
    const first = Array.isArray(dates) && dates.length ? dates[0] : null
    const monthHasDaily = !!first
    const dailySales = Number(byMonth?.[em]?.salesPlan || 0)
    const dailyMarkdown = Number(byMonth?.[em]?.markdownPlan || 0)
    const dailyBom = first ? Number(plannerSession?.dailyByDate?.get?.(first)?.inventory ?? 0) : 0
    const rawRow = monthlyActualRows.find((row) => Number(row?.plan_epoch_month ?? row?.epoch_month ?? row?.epochMonth ?? row?.planEpochMonth) === Number(em)) || null
    const useDailyForMonth = shouldUseDailyMonthValues({
      monthHasDaily,
      dailySales,
      dailyMarkdown,
      dailyBom,
      monthlyRow: hasMeaningfulMonthlyPlanValues(m) ? m : rawRow,
    })

    return {
      ...m,
      salesPlan: useDailyForMonth ? dailySales : Number(m?.salesPlan ?? 0),
      markdownPlan: useDailyForMonth ? dailyMarkdown : Number(m?.markdownPlan ?? 0),
      planInvBOM: (() => {
        const hasExplicitInventoryOverride = hasExplicitPlanInventoryOverrideForMonth({
          epochMonth: em,
          monthlyEditsByMonth,
          weeklyEditsByWeek,
          weekToMonthDayCounts,
        })
        if (useDailyForMonth && !hasExplicitInventoryOverride && dailyBom > 0) return dailyBom
        return Number(m?.planInvBOM ?? 0)
      })(),
    }
  })
}

export const buildRearDisplayEpochWeeks = ({ editableEpochWeeks, plannerSession }) => {
  const safeEditableEpochWeeks = Array.isArray(editableEpochWeeks)
    ? editableEpochWeeks.map((week) => Number(week)).filter((week) => Number.isFinite(week) && week > 0)
    : []

  if (!safeEditableEpochWeeks.length) return []

  const anchorWeek = Number(safeEditableEpochWeeks[0])
  const datesByWeek = plannerSession?.datesByWeek
  const datesByMonth = plannerSession?.datesByMonth
  const dailyByDate = plannerSession?.dailyByDate
  const weekToMonthDayCounts = plannerSession?.weekToMonthDayCounts

  const anchorWeekDates = datesByWeek?.get?.(String(anchorWeek)) || []
  let anchorEpochMonth = null

  if (anchorWeekDates.length && dailyByDate?.get) {
    anchorEpochMonth = Number(dailyByDate.get(anchorWeekDates[0])?.epochMonth ?? 0) || null
  }

  if (!anchorEpochMonth && weekToMonthDayCounts?.get) {
    const monthMap = weekToMonthDayCounts.get(anchorWeek) || weekToMonthDayCounts.get(String(anchorWeek)) || null
    if (monthMap?.size) {
      const ordered = Array.from(monthMap.entries()).sort((a, b) => {
        const dayDiff = Number(b?.[1] || 0) - Number(a?.[1] || 0)
        if (dayDiff !== 0) return dayDiff
        return Number(a?.[0] || 0) - Number(b?.[0] || 0)
      })
      anchorEpochMonth = Number(ordered?.[0]?.[0] ?? 0) || null
    }
  }


  if (!anchorEpochMonth || !datesByMonth?.get || !dailyByDate?.get) return []

  const monthDates = datesByMonth.get(String(anchorEpochMonth)) || []
  const monthWeeks = Array.from(new Set(
    monthDates
      .map((date) => Number(dailyByDate.get(date)?.epochWeek ?? 0))
      .filter((week) => Number.isFinite(week) && week > 0)
  )).sort((a, b) => a - b)

  return monthWeeks.filter((week) => week < anchorWeek).map((week) => String(week))
}

export const resolveWeeklyActualSeries = ({
  epochWeeks = [],
  rawByWeek = new Map(),
  fallbackByWeek = {},
  rearHistoricalWeekSet = new Set(),
}) => {
  return (Array.isArray(epochWeeks) ? epochWeeks : []).map((week) => {
    const weekKey = String(week)
    const hasRawWeek = typeof rawByWeek?.has === 'function' ? rawByWeek.has(weekKey) : false
    if (hasRawWeek) {
      return Number(rawByWeek?.get?.(weekKey) ?? 0)
    }
    if (rearHistoricalWeekSet?.has?.(weekKey)) {
      return Number(fallbackByWeek?.[weekKey] ?? 0)
    }
    return Number(fallbackByWeek?.[weekKey] ?? 0)
  })
}

export const buildTailDisplayEpochWeeks = ({ editableEpochWeeks, plannerSession }) => {
  const safeEditableEpochWeeks = Array.isArray(editableEpochWeeks)
    ? editableEpochWeeks.map((week) => Number(week)).filter((week) => Number.isFinite(week) && week > 0)
    : []

  if (!safeEditableEpochWeeks.length) return []

  const lastWeek = safeEditableEpochWeeks[safeEditableEpochWeeks.length - 1]
  const datesByWeek = plannerSession?.datesByWeek
  const datesByMonth = plannerSession?.datesByMonth
  const dailyByDate = plannerSession?.dailyByDate
  const weekToMonthDayCounts = plannerSession?.weekToMonthDayCounts

  const lastWeekDates = datesByWeek?.get?.(String(lastWeek)) || []
  let lastEpochMonth = null

  if (lastWeekDates.length && dailyByDate?.get) {
    lastEpochMonth = Number(dailyByDate.get(lastWeekDates[0])?.epochMonth ?? 0) || null
  }

  if (!lastEpochMonth && weekToMonthDayCounts?.get) {
    const monthMap = weekToMonthDayCounts.get(lastWeek) || weekToMonthDayCounts.get(String(lastWeek)) || null
    if (monthMap?.size) {
      const ordered = Array.from(monthMap.entries()).sort((a, b) => {
        const dayDiff = Number(b?.[1] || 0) - Number(a?.[1] || 0)
        if (dayDiff !== 0) return dayDiff
        return Number(a?.[0] || 0) - Number(b?.[0] || 0)
      })
      lastEpochMonth = Number(ordered?.[0]?.[0] ?? 0) || null
    }
  }

  if (!lastEpochMonth || !datesByMonth?.get || !dailyByDate?.get) return []

  const monthDates = datesByMonth.get(String(lastEpochMonth)) || []
  const monthWeeks = Array.from(new Set(
    monthDates
      .map((date) => Number(dailyByDate.get(date)?.epochWeek ?? 0))
      .filter((week) => Number.isFinite(week) && week > 0)
  )).sort((a, b) => a - b)

  return monthWeeks.filter((week) => week > lastWeek).map((week) => String(week))
}

export const buildVisibleWeeklyEpochWeeks = ({ row, plannerSession, epochWeek }) => {
  const start = Number(epochWeek || row?.epoch_week || 0)
  const editableEpochWeeks = start > 0
    ? Array.from({ length: 12 }, (_, idx) => String(start + idx))
    : (row?.EpochWeeksL12W ? String(row.EpochWeeksL12W).split(',') : []).map((week) => String(week).trim()).filter(Boolean)

  const rearEpochWeeks = buildRearDisplayEpochWeeks({
    editableEpochWeeks,
    plannerSession,
  })

  const tailEpochWeeks = buildTailDisplayEpochWeeks({
    editableEpochWeeks,
    plannerSession,
  })

  return [...rearEpochWeeks, ...editableEpochWeeks, ...tailEpochWeeks]
}

export function rollForwardValuesToCurrentWeek({ epochWeeks, currentEpochWeek, values }) {
  const safeEpochWeeks = Array.isArray(epochWeeks) ? epochWeeks.map((week) => String(week)) : []
  const normalizedValues = Array.isArray(values)
    ? values.map((value) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    })
    : []

  if (!safeEpochWeeks.length) return normalizedValues

  const currentIndex = safeEpochWeeks.findIndex((week) => String(week) === String(currentEpochWeek))
  if (currentIndex <= 0) return normalizedValues

  const carryForward = normalizedValues
    .slice(0, currentIndex)
    .reduce((acc, value) => acc + Number(value || 0), 0)

  return normalizedValues.map((value, idx) => {
    if (idx < currentIndex) return 0
    if (idx === currentIndex) return Number(value || 0) + carryForward
    return Number(value || 0)
  })
}

export function calculateWeeklyFreshnessFactor({ bowInventory, priorThreeWeekInflows }) {
  const inventory = Number(bowInventory)
  if (!Number.isFinite(inventory)) return null

  const denominator = (Array.isArray(priorThreeWeekInflows) ? priorThreeWeekInflows : [])
    .reduce((acc, value) => acc + (Number.isFinite(Number(value)) ? Number(value) : 0), 0)

  if (!(denominator > 0)) return null
  return inventory / denominator
}

export function deriveWeeklyInflowsFromPlanSeries({
  salesPlanSeries = [],
  markdownPlanSeries = [],
  invPlanSeries = [],
  fallbackInflowRetailSeries = [],
}) {
  const maxLen = Math.max(
    Array.isArray(salesPlanSeries) ? salesPlanSeries.length : 0,
    Array.isArray(markdownPlanSeries) ? markdownPlanSeries.length : 0,
    Array.isArray(invPlanSeries) ? invPlanSeries.length : 0,
    Array.isArray(fallbackInflowRetailSeries) ? fallbackInflowRetailSeries.length : 0,
  )

  return Array.from({ length: maxLen }, (_, idx) => {
    const currentInv = Number(invPlanSeries?.[idx] ?? 0)
    const salesPlan = Number(salesPlanSeries?.[idx] ?? 0)
    const markdownPlan = Number(markdownPlanSeries?.[idx] ?? 0)

    if (idx < maxLen - 1) {
      const nextInv = Number(invPlanSeries?.[idx + 1] ?? currentInv)
      return Number((nextInv - currentInv + salesPlan + markdownPlan).toFixed(2))
    }

    return Number(fallbackInflowRetailSeries?.[idx] ?? 0)
  })
}

function PlanEditWeeklyTableShell({ calcMode, annualHeaderCollapsed = false, onAnnualHeaderCollapsedChange, storageKeySeed, clientId, locationClassId, epochWeek, weeklyPlanSettingsId, monthlyPlanSettingsId = null, onChartData, onChartLabels, onPeriodData, plannerSession, onWeeklyEdit, onWeeklyContextLoaded, onDailyByDateChange }) {
  const messageContext = TipContext.useMessage()

  const toImuRate = (value) => {
    const n = Number(value || 0)
    if (!Number.isFinite(n) || n <= 0) return 0
    return n > 1 ? (n / 100) : n
  }

  const applyFirstDayInventory = (byDate, dates, nextValue) => {
    if (!byDate || typeof byDate.get !== 'function' || !Array.isArray(dates) || !dates.length) return byDate
    const nextDaily = new Map(byDate)
    const firstDate = dates[0]
    const record = nextDaily.get(firstDate)
    if (!record) return byDate
    nextDaily.set(firstDate, {
      ...record,
      inventory: Math.max(0, Number(nextValue || 0)),
    })
    return nextDaily
  }


  const commitDailyMutation = (nextDaily) => {
    if (!nextDaily) return
    if (typeof onDailyByDateChange === 'function') onDailyByDateChange(nextDaily)
    setDirty(true)
  }

  const plannerSessionRef = useRef(plannerSession)
  useEffect(() => {
    plannerSessionRef.current = plannerSession
  }, [plannerSession])

  const storageKey = useMemo(() => {
    const seed = storageKeySeed || 'weeklyDraft:unknown'
    return `planEdit.weeklyDraft.${seed}.${calcMode || 'allocate'}`
  }, [storageKeySeed, calcMode])

  const [dirty, setDirty] = useState(false)
  const [loading, setLoading] = useState(false)
  const [debugInfo, setDebugInfo] = useState(null)
  const [apiRow, setApiRow] = useState(null)
  const [seedSource, setSeedSource] = useState(null)
  const hasAutoSeededRef = useRef(false)

  // Seed weekly plan dollars from the monthly base window using day-weights.
  // All key inputs must be passed explicitly - never rely on ref which may be stale.
  const computeSeedFromMonthlyBase = ({ epochWeeks, weeklyInvActual, monthlyBaseUi: passedMonthlyBaseUi, weekToMonthDayCounts: passedW2M, weeklyActualSalesByWeek: passedActuals }) => {
     const ps = plannerSessionRef.current
     const weeklyActualSalesByWeek = passedActuals || ps?.weeklyActualSalesByWeek || {}
     const resolvedMonthlyBaseUi = Array.isArray(passedMonthlyBaseUi) && passedMonthlyBaseUi.length
       ? passedMonthlyBaseUi
       : (Array.isArray(ps?.monthlyBaseUi) ? ps.monthlyBaseUi : [])

    const baseByEm = new Map()
    ;(resolvedMonthlyBaseUi).forEach((m) => {
      const em = Number(m?.epochMonth)
      if (Number.isFinite(em)) baseByEm.set(em, m)
    })

    const weekToMonthDayCounts = (passedW2M && passedW2M.size > 0) ? passedW2M : (ps?.weekToMonthDayCounts || new Map())
    const outByWeek = seedWeeklyPlansFromMonthlyBase({
      epochWeeks,
      monthlyBaseUi: resolvedMonthlyBaseUi,
      weekToMonthDayCounts,
      weeklyActualSalesByWeek,
    })

    // DEBUG
    console.debug('[planner][seed] computeSeed diag', {
      w2mSize: weekToMonthDayCounts.size,
      passedW2MSize: passedW2M ? passedW2M.size : 'none',
      outByWeekKeys: Object.keys(outByWeek),
      baseByEmKeys: Array.from(baseByEm.keys()),
      resolvedMonthlyBaseUiLen: resolvedMonthlyBaseUi.length,
      epochWeeks,
    })

    const seedSales = []
    const seedMd = []
    const seedInv = []
    const seedPlanPct = []
    const seedMdPct = []

     // For inventory: use actual where non-zero, otherwise fall back to the dominant month's planInvBOM.
     const getMonthInvBOM = (m) => Number(m?.planInvBOM ?? m?.inventory_bom_plan ?? m?.inventoryBomPlan ?? 0)

     epochWeeks.forEach((ew, idx) => {
       const rolledWeek = outByWeek?.[String(ew)] || {}
       const salesPlan = Number(rolledWeek?.salesPlan ?? 0)
       const mdPlan = Number(rolledWeek?.markdownPlan ?? 0)
       seedSales[idx] = Number.isFinite(salesPlan) ? salesPlan : 0
       seedMd[idx] = Number.isFinite(mdPlan) ? mdPlan : 0
        seedPlanPct[idx] = Number.isFinite(Number(rolledWeek?.planPercent)) ? Number(rolledWeek?.planPercent) : 0
        seedMdPct[idx] = Number.isFinite(Number(rolledWeek?.markdownPlanPercent)) ? Number(rolledWeek?.markdownPlanPercent) : 0
        const dominantEpochMonth = getDominantEpochMonthForWeek({
          ew,
          weekToMonthDayCounts,
          fallbackEpochMonth: Number(resolvedMonthlyBaseUi?.[0]?.epochMonth ?? 0) || null,
        })
        const dominantMonth = resolvedMonthlyBaseUi.find((m) => Number(m?.epochMonth) === Number(dominantEpochMonth)) || resolvedMonthlyBaseUi[0] || null
        const dominantMonthInvBOM = getMonthInvBOM(dominantMonth)
        // Use actual inventory if available, otherwise use the dominant month's BOM plan as seed.
       const actualInv = Number(weeklyInvActual?.[idx] ?? 0)
        seedInv[idx] = actualInv !== 0 ? actualInv : dominantMonthInvBOM
     })

    return { seedSales, seedMd, seedInv, seedPlanPct, seedMdPct }
  }

  const draftStorageKey = useMemo(() => `${storageKey}.draftV1`, [storageKey])
  const sharedDraftKey = useMemo(() => {
    return makePlannerDraftKey({ clientId, locationClassId, monthlyPlanSettingsId, weeklyPlanSettingsId })
  }, [clientId, locationClassId, monthlyPlanSettingsId, weeklyPlanSettingsId])

  const canLoadFromApi = useMemo(() => {
    return !!(clientId && locationClassId && weeklyPlanSettingsId && monthlyPlanSettingsId)
  }, [clientId, locationClassId, weeklyPlanSettingsId, monthlyPlanSettingsId])

  const csvToNums = (s) => {
    if (s === null || typeof s === 'undefined') return []
    const str = String(s)
    if (!str.length) return []
    return str.split(',').map((v) => (v === '' ? null : Number(v)))
  }

  const getDisplayEpochWeeks = (row) => {
    const start = Number(epochWeek || row?.epoch_week || 0)
    if (start > 0) return Array.from({ length: 12 }, (_, idx) => String(start + idx))
    return (row?.EpochWeeksL12W ? String(row.EpochWeeksL12W).split(',') : []).map((w) => String(w).trim()).filter(Boolean)
  }

  const getActualEpochWeeks = (row) => {
    const source = row?.ActualEpochWeeksL12W ?? null
    if (source) return String(source).split(',').map((w) => String(w).trim()).filter(Boolean)
    return getDisplayEpochWeeks(row)
  }

  const getVisibleActualEpochWeeks = (row) => {
    const source = row?.VisibleActualEpochWeeks ?? null
    if (source) return String(source).split(',').map((w) => String(w).trim()).filter(Boolean)
    return getActualEpochWeeks(row)
  }

  const getVisibleEpochWeeks = (row) => buildVisibleWeeklyEpochWeeks({ row, plannerSession, epochWeek })

  const [draft, setDraft] = useState(() => ({
    salesPlan: [],
    mdPlan: [],
    invPlan: [],
    planPercent: [],
    planSS: [],
    mdPlanPercent: [],
  }))

  const resetDraft = () => {
    try {
      localStorage.removeItem(draftStorageKey)
    } catch (e) {}
    setDirty(false)
    setDraft({
      salesPlan: [],
      mdPlan: [],
      invPlan: [],
      planPercent: [],
      planSS: [],
      mdPlanPercent: [],
    })
  }

  // Load weekly plan context; API is the source of truth on initial load.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!canLoadFromApi) return
      try {
        setLoading(true)
        const payload = await PlanService.getWeeklyPlanContext(clientId, {
          locationClassId,
          epochWeek,
          weeklyPlanSettingsId,
          monthlyPlanSettingsId,
        })
        const data = payload?.data ?? payload
        const row = Array.isArray(data) ? data[0] : data
        if (!row || typeof row !== 'object') {
          if (!cancelled) {
            setApiRow(null)
            setDebugInfo({ hasRow: false, keys: [], weeklyPlanVersion: null, monthlyPlanVersion: null })
          }
          messageContext.show('Weekly plan context returned no row')
          return
        }
         if (cancelled) return
         setDebugInfo({
           hasRow: !!row,
           keys: row ? Object.keys(row) : [],
           weeklyPlanVersion: row?.weekly_plan_version,
           monthlyPlanVersion: row?.monthly_plan_version,
         })
         setApiRow(row || {})

        // Always clobber drafts on load/refresh to avoid haunted scenarios.
        clearPlannerDraft(sharedDraftKey)
        try {
          localStorage.removeItem(draftStorageKey)
        } catch (e) {}
        setDirty(false)
        hasAutoSeededRef.current = false

        const weeklySalesPlan = csvToNums(row?.WeeklySalesPlanL12W)
        const weeklyMdPlan = csvToNums(row?.WeeklyMarkdownsPlanL12W)
        const weeklyInvPlan = csvToNums(row?.BeginWeekInventoryPlanL12W)

        // Notify parent of epoch weeks and actuals so the save payload has the correct anchor.
        const loadedEpochWeeks = getDisplayEpochWeeks(row)
        const visibleActualEpochWeeks = getVisibleActualEpochWeeks(row)
        const salesActualByWeek = {}
        const salesActualArr = csvToNums(row?.VisibleActualSalesL12W ?? row?.ActualSalesL12W ?? row?.SalesL12W)
        const invActualArr = csvToNums(row?.VisibleActualInventoryL12W ?? row?.ActualInventoryL12W ?? row?.InventoryL12W)
        const invActualByWeek = {}
        visibleActualEpochWeeks.forEach((ew, i) => {
          salesActualByWeek[ew] = salesActualArr[i] ?? 0
          invActualByWeek[ew] = invActualArr[i] ?? 0
        })
        const weeklyInvActual = loadedEpochWeeks.map((ew) => Number(invActualByWeek[ew] ?? 0))
        if (typeof onWeeklyContextLoaded === 'function') {
          onWeeklyContextLoaded({
            epochWeeks: loadedEpochWeeks,
            salesActualByWeek,
            anchorEpochWeek: Number(row?.epoch_week || epochWeek || 0) || null,
          })
        }

        const allZero = (arr) => Array.isArray(arr) && arr.length > 0 && arr.every((v) => Number(v || 0) === 0)
        const weeklyHasData = (!allZero(weeklySalesPlan) && weeklySalesPlan.length > 0)

        // If stored weekly plans already have real data, use them directly.
        // Otherwise, the cascade useEffect will seed from monthly once it arrives.
        if (weeklyHasData) {
          setDraft({
            salesPlan: weeklySalesPlan,
            mdPlan: weeklyMdPlan,
            invPlan: weeklyInvPlan.length ? weeklyInvPlan : weeklyInvActual,
            planPercent: [],
            planSS: [],
            mdPlanPercent: [],
          })
          setSeedSource('weekly')
          hasAutoSeededRef.current = true
        }
      } catch (e) {
        // Ignore preflight + aborted/cancelled requests (React StrictMode can trigger double-invocation).
        const msg = String(e?.message || '')
        const isAbort = e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || msg.toLowerCase().includes('aborted')
        if (isAbort) return
        if (e?.response?.status === 204 && String(e?.config?.method || '').toLowerCase() === 'options') return

        messageContext.show(e?.response?.data?.message || e?.message || 'Failed to load weekly plan context')
        } finally {
          if (!cancelled) setLoading(false)
        }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [canLoadFromApi, clientId, locationClassId, epochWeek, weeklyPlanSettingsId, monthlyPlanSettingsId, sharedDraftKey, draftStorageKey, calcMode])

  // Shared draft persistence is intentionally disabled: refresh should clear scenarios.
  // (We keep local component state for tab switching because the component stays mounted.)

  // Persist draft only after user edits (dirty=true).
  useEffect(() => {
    if (!dirty) return
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(draft || {}))
    } catch (e) {}
  }, [draftStorageKey, draft, dirty])

  // NOTE: The grid is driven by `shapePeriods()` + `WeeklyPlanningGrid`.
  // We no longer keep parallel series memoizations here (they caused runtime errors and are redundant).

  const shapePeriods = (row) => {
    const editableEpochWeeks = getDisplayEpochWeeks(row)
    const epochWeeks = getVisibleEpochWeeks(row)
    const actualEpochWeeks = getActualEpochWeeks(row)
    const visibleActualEpochWeeks = getVisibleActualEpochWeeks(row)
    const editableWeekSet = new Set(editableEpochWeeks.map((week) => String(week)))
    // Split non-editable weeks: rear historical (before editable window) vs tail (after editable window, completing last month)
    const firstEditableWeek = editableEpochWeeks.length ? Number(editableEpochWeeks[0]) : Infinity
    const lastEditableWeek = editableEpochWeeks.length ? Number(editableEpochWeeks[editableEpochWeeks.length - 1]) : -Infinity
    const rearHistoricalWeekSet = new Set(
      epochWeeks.filter((week) => !editableWeekSet.has(String(week)) && Number(week) < firstEditableWeek).map((week) => String(week))
    )
    const tailWeekSet = new Set(
      epochWeeks.filter((week) => !editableWeekSet.has(String(week)) && Number(week) > lastEditableWeek).map((week) => String(week))
    )
    const editableWeekIndexByKey = new Map(editableEpochWeeks.map((week, idx) => [String(week), idx]))

     const salesActualRaw = csvToNums(row?.VisibleActualSalesL12W ?? row?.ActualSalesL12W ?? row?.SalesL12W)
     const mdActualRaw = csvToNums(row?.VisibleActualMarkdownsL12W ?? row?.ActualMarkdownsL12W ?? row?.MarkdownsL12W)
     const invActualRaw = csvToNums(row?.VisibleActualInventoryL12W ?? row?.ActualInventoryL12W ?? row?.InventoryL12W)
     const actualWeekKeys = visibleActualEpochWeeks.length ? visibleActualEpochWeeks : actualEpochWeeks
     const salesActualRawByWeek = new Map(actualWeekKeys.map((week, idx) => [String(week), Number(salesActualRaw[idx] ?? 0)]))
     const markdownActualRawByWeek = new Map(actualWeekKeys.map((week, idx) => [String(week), Number(mdActualRaw[idx] ?? 0)]))
     const inventoryActualRawByWeek = new Map(actualWeekKeys.map((week, idx) => [String(week), Number(invActualRaw[idx] ?? 0)]))

     const monthlyActualInvByEm = new Map()
     const monthlyActualSalesByEm = new Map()
     const monthlyActualMdByEm = new Map()
     const monthlyActualInflowRetailByEm = new Map()
     const monthlyActualInflowCostByEm = new Map()
     ;(Array.isArray(plannerSession?.monthlyBase) ? plannerSession.monthlyBase : []).forEach((m) => {
       const em = Number(m?.plan_epoch_month ?? m?.epoch_month ?? m?.epochMonth)
       if (!Number.isFinite(em)) return
       monthlyActualSalesByEm.set(em, Number(m?.sales_actual ?? m?.salesActual ?? 0))
       monthlyActualMdByEm.set(em, Number(m?.markdown_actual ?? m?.markdownActual ?? 0))
       const actualInv = Number(
         m?.inventory_actual_bom ?? m?.inventoryActualBom ?? m?.inventory_bom_actual ?? m?.inventoryBomActual ?? 0
       )
       monthlyActualInvByEm.set(em, actualInv)
       monthlyActualInflowRetailByEm.set(em, Number(m?.inflow_retail_actual ?? m?.inflowRetailActual ?? m?.inflowRetail ?? 0))
       monthlyActualInflowCostByEm.set(em, Number(m?.inflow_cost_actual ?? m?.inflowCostActual ?? m?.inflowCost ?? 0))
     })

     const primaryMonthForWeek = (ew) => {
       const mMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(ew)) || plannerSession?.weekToMonthDayCounts?.get?.(String(ew))
       if (!mMap || !mMap.size) return null
       const sorted = Array.from(mMap.entries()).sort((a, b) => {
         const daysDiff = Number(b[1] || 0) - Number(a[1] || 0)
         if (daysDiff !== 0) return daysDiff
         return Number(a[0]) - Number(b[0])
       })
       return Number(sorted[0]?.[0] ?? 0) || null
     }

     const headerWeekLabelForWeek = (ew) => {
       const dates = plannerSession?.datesByWeek?.get?.(String(ew)) || []
       const firstDate = Array.isArray(dates) && dates.length ? dates[0] : null
       return formatWeekStartLabel(firstDate, ew)
     }

     const representativeMonthMetaForWeek = (ew) => {
       const dates = plannerSession?.datesByWeek?.get?.(String(ew)) || []
       const firstDate = Array.isArray(dates) && dates.length ? dates[0] : null
       const monthMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(ew)) || plannerSession?.weekToMonthDayCounts?.get?.(String(ew)) || new Map()
       const dominantEpochMonth = primaryMonthForWeek(ew)
       const daysInRepresentativeMonth = dominantEpochMonth ? Number(monthMap?.get?.(dominantEpochMonth) ?? monthMap?.get?.(String(dominantEpochMonth)) ?? 0) : 0
       const fallbackLabel = formatMonthLabelWithSaturdayComparison(
         dominantEpochMonth,
         firstDate,
         formatRepresentativeMonthLabel(dominantEpochMonth, firstDate)
       )

       return {
         representativeEpochMonth: dominantEpochMonth,
         representativeMonthKey: dominantEpochMonth ? String(dominantEpochMonth) : '',
         representativeMonthLabel: fallbackLabel,
         representativeMonthDayCount: daysInRepresentativeMonth,
         isPartialWeekInMonth: daysInRepresentativeMonth > 0 && daysInRepresentativeMonth < 7,
       }
     }

      const buildMonthSegmentsForWeek = ({ ew, salesPlanVal, mdPlanVal, planInvBOMVal, actualInvBOMVal, onOrderRetailVal, onOrderCostVal }) => {
       const dates = plannerSession?.datesByWeek?.get?.(String(ew)) || []
       const rawMonthMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(ew)) || plannerSession?.weekToMonthDayCounts?.get?.(String(ew)) || new Map()

       // Detect if this is the first epoch week in the planning window with fewer than 7 tracked
       // days. daily_values only starts from the first day of the first planning month (e.g. April 1),
       // so a week that straddles the prior month boundary (e.g. Mar 30 – Apr 5) will be missing
       // the prior-month day counts. Synthesise those days using the preceding calendar month so the
       // week renders its full 7-day context as a proper split column.
       const firstEpochWeek = epochWeeks.length > 0 ? Math.min(...epochWeeks.map(Number)) : Infinity
       let monthMap = rawMonthMap
       if (Number(ew) === firstEpochWeek && rawMonthMap.size > 0) {
         const trackedDays = Array.from(rawMonthMap.values()).reduce((acc, d) => acc + Number(d || 0), 0)
         if (trackedDays > 0 && trackedDays < 7) {
           const earliestTrackedMonth = Math.min(...Array.from(rawMonthMap.keys()).map(Number))
           // Compute prior calendar month in YYYYMM format (e.g. 202404 → 202403; 202401 → 202312).
           const priorEm = (() => {
             const n = earliestTrackedMonth
             if (n >= 190001 && n <= 299912) {
               const yr = Math.floor(n / 100)
               const mo = n % 100
               return mo === 1 ? (yr - 1) * 100 + 12 : yr * 100 + (mo - 1)
             }
             return n - 1
           })()
           monthMap = new Map(rawMonthMap)
           monthMap.set(priorEm, 7 - trackedDays)
         }
       }

       const totalDays = Array.from(monthMap.values()).reduce((acc, days) => acc + Number(days || 0), 0)

       const orderedEpochMonths = Array.from(monthMap.keys()).sort((a, b) => {
         const aFirstIdx = dates.findIndex((dt) => Number(plannerSession?.dailyByDate?.get?.(dt)?.epochMonth) === Number(a))
         const bFirstIdx = dates.findIndex((dt) => Number(plannerSession?.dailyByDate?.get?.(dt)?.epochMonth) === Number(b))
         // When both months have matching dates in the week, sort by first appearance.
         if (aFirstIdx !== -1 && bFirstIdx !== -1) return aFirstIdx - bFirstIdx
         // Otherwise use chronological (numeric epoch month) order so that a prior-month stub
         // (no dates in our data) correctly sorts BEFORE its successor month.
         return Number(a) - Number(b)
       })

        const baseSegmentOnOrderRetail = orderedEpochMonths.map((em) => {
          const monthRef = effectiveMonthsByEm.get(Number(em)) || null
          const dayCount = Number(monthMap?.get?.(em) ?? monthMap?.get?.(String(em)) ?? 0)
          const monthDays = Number(monthDayTotals.get(String(em)) || 0)
          return monthDays > 0
            ? Number(monthRef?.onOrderRetailMtd ?? 0) * (dayCount / monthDays)
            : 0
        })
        const baseSegmentOnOrderCost = orderedEpochMonths.map((em) => {
          const monthRef = effectiveMonthsByEm.get(Number(em)) || null
          const dayCount = Number(monthMap?.get?.(em) ?? monthMap?.get?.(String(em)) ?? 0)
          const monthDays = Number(monthDayTotals.get(String(em)) || 0)
          return monthDays > 0
            ? Number(monthRef?.onOrderCostMtd ?? 0) * (dayCount / monthDays)
            : 0
        })
        const totalBaseSegmentOnOrderRetail = baseSegmentOnOrderRetail.reduce((acc, value) => acc + Number(value || 0), 0)
        const totalBaseSegmentOnOrderCost = baseSegmentOnOrderCost.reduce((acc, value) => acc + Number(value || 0), 0)

        return orderedEpochMonths.map((em, idx) => {
         const monthKey = String(em)
         const segmentDates = dates.filter((dt) => Number(plannerSession?.dailyByDate?.get?.(dt)?.epochMonth) === Number(em))
         const dayCount = Number(monthMap?.get?.(em) ?? monthMap?.get?.(monthKey) ?? segmentDates.length ?? 0)
         const share = totalDays > 0 ? (dayCount / totalDays) : (orderedEpochMonths.length ? (1 / orderedEpochMonths.length) : 1)
         const monthLabel = formatMonthLabelWithSaturdayComparison(
           em,
           segmentDates?.[0] || dates?.[0] || null,
           formatRepresentativeMonthLabel(em, segmentDates?.[0] || dates?.[0] || null)
         )
         const monthRef = effectiveMonthsByEm.get(Number(em)) || null
         const segmentSalesPlan = segmentDates.length
           ? segmentDates.reduce((acc, dt) => acc + Number(plannerSession?.dailyByDate?.get?.(dt)?.salesPlan || 0), 0)
           : Number(salesPlanVal || 0) * share
         const segmentMarkdownPlan = segmentDates.length
           ? segmentDates.reduce((acc, dt) => acc + Number(plannerSession?.dailyByDate?.get?.(dt)?.markdownPlan || 0), 0)
           : Number(mdPlanVal || 0) * share
         const segmentInvBow = segmentDates.length
           ? Number(plannerSession?.dailyByDate?.get?.(segmentDates[0])?.inventory ?? monthRef?.planInvBOM ?? 0)
           : (idx === 0 ? Number(planInvBOMVal || 0) : Number(monthRef?.planInvBOM ?? planInvBOMVal ?? 0))
          const segmentOnOrderRetailShare = totalBaseSegmentOnOrderRetail > 0
            ? (Number(baseSegmentOnOrderRetail[idx] || 0) / totalBaseSegmentOnOrderRetail)
            : share
          const segmentOnOrderCostShare = totalBaseSegmentOnOrderCost > 0
            ? (Number(baseSegmentOnOrderCost[idx] || 0) / totalBaseSegmentOnOrderCost)
            : share
          const segmentOnOrderRetailMtd = Number(onOrderRetailVal || 0) * segmentOnOrderRetailShare
          const segmentOnOrderCostMtd = Number(onOrderCostVal || 0) * segmentOnOrderCostShare
          const weekIdx = epochWeeks.findIndex((week) => String(week) === String(ew))
          const segmentSalesActual = Number((weekIdx >= 0 ? salesActual?.[weekIdx] : salesActualRawByWeek.get(String(ew))) ?? 0) * share
          const segmentMarkdownActual = Number((weekIdx >= 0 ? mdActual?.[weekIdx] : markdownActualRawByWeek.get(String(ew))) ?? 0) * share
          const segmentActualInvBow = idx === 0
            ? Number(actualInvBOMVal ?? 0)
            : Number(monthlyActualInvByEm.get(Number(em)) ?? actualInvBOMVal ?? 0)

         return {
           monthKey,
           monthLabel,
           days: dayCount,
           dates: segmentDates,
           salesPlan: segmentSalesPlan,
            salesActual: segmentSalesActual,
           markdownPlan: segmentMarkdownPlan,
            markdownActual: segmentMarkdownActual,
            onOrderRetailMtd: segmentOnOrderRetailMtd,
            onOrderCostMtd: segmentOnOrderCostMtd,
           planInvBOM: segmentInvBow,
            actualInvBOM: segmentActualInvBow,
           salesPlanMonthlyReference: monthRef ? Number(monthRef?.salesPlan ?? 0) : null,
           planInvBOMMonthlyReference: monthRef ? Number(monthRef?.planInvBOM ?? 0) : null,
           markdownPlanMonthlyReference: monthRef ? Number(monthRef?.markdownPlan ?? 0) : null,
         }
       }).filter((segment) => Number(segment?.days || 0) > 0)
     }

      const salesActualFallbackByWeek = allocateMonthlySalesActualsToWeeks({
        epochWeeks,
        weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
        monthlyActualSalesByEm,
      })
      const salesActual = resolveWeeklyActualSeries({
        epochWeeks,
        rawByWeek: salesActualRawByWeek,
        fallbackByWeek: salesActualFallbackByWeek,
        rearHistoricalWeekSet,
      })
      const markdownActualFallbackByWeek = allocateMonthlyMarkdownActualsToWeeks({
        epochWeeks,
        weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
        monthlyActualMarkdownByEm: monthlyActualMdByEm,
        monthlyActualSalesByEm,
        weeklySalesActualByWeek: Object.fromEntries(epochWeeks.map((ew, idx) => [String(ew), Number(salesActual[idx] ?? 0)])),
      })
      const mdActual = resolveWeeklyActualSeries({
        epochWeeks,
        rawByWeek: markdownActualRawByWeek,
        fallbackByWeek: markdownActualFallbackByWeek,
        rearHistoricalWeekSet,
      })
      const inventoryActualFallbackByWeek = Object.fromEntries(epochWeeks.map((ew) => {
        const primaryMonth = primaryMonthForWeek(ew)
        return [String(ew), primaryMonth ? Number(monthlyActualInvByEm.get(primaryMonth) ?? 0) : 0]
      }))
      const invActual = resolveWeeklyActualSeries({
        epochWeeks,
        rawByWeek: inventoryActualRawByWeek,
        fallbackByWeek: inventoryActualFallbackByWeek,
        rearHistoricalWeekSet,
      })
      const weeklyInvActual = invActual

     const inflowRetailActualFallbackByWeek = allocateMonthlyInflowActualsToWeeks({
       epochWeeks,
       weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
       monthlyActualInflowByEm: monthlyActualInflowRetailByEm,
     })
     const inflowCostActualFallbackByWeek = allocateMonthlyInflowActualsToWeeks({
       epochWeeks,
       weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
       monthlyActualInflowByEm: monthlyActualInflowCostByEm,
     })
     const inflowRetailActualRaw = csvToNums(row?.VisibleActualInflowRetailL12W ?? '')
     const inflowCostActualRaw = csvToNums(row?.VisibleActualInflowCostL12W ?? '')
     const inflowRetailActualRawByWeek = new Map(actualWeekKeys.map((week, idx) => [String(week), Number(inflowRetailActualRaw[idx] ?? 0)]))
     const inflowCostActualRawByWeek = new Map(actualWeekKeys.map((week, idx) => [String(week), Number(inflowCostActualRaw[idx] ?? 0)]))
     const inflowRetailActualByWeek = resolveWeeklyActualSeries({
       epochWeeks,
       rawByWeek: inflowRetailActualRawByWeek,
       fallbackByWeek: inflowRetailActualFallbackByWeek,
       rearHistoricalWeekSet,
     })
     const inflowCostActualByWeek = resolveWeeklyActualSeries({
       epochWeeks,
       rawByWeek: inflowCostActualRawByWeek,
       fallbackByWeek: inflowCostActualFallbackByWeek,
       rearHistoricalWeekSet,
     })
      const weeklyInvPlanRaw = csvToNums(row?.BeginWeekInventoryPlanL12W)
      const invPlanFromApiByWeek = new Map(editableEpochWeeks.map((week, idx) => [String(week), Number(weeklyInvPlanRaw[idx] ?? 0)]))
      const invPlanFromApi = epochWeeks.map((ew) => Number(invPlanFromApiByWeek.get(String(ew)) ?? 0))

    // Use draft if it has real values; otherwise fall back to render-time seed from monthlyBaseUi.
    const draftSales = epochWeeks.map((ew) => {
      const editableIdx = editableWeekIndexByKey.get(String(ew))
      return editableIdx === undefined ? 0 : (Array.isArray(draft?.salesPlan) ? Number(draft.salesPlan[editableIdx] ?? 0) : 0)
    })
    const draftMd    = epochWeeks.map((ew) => {
      const editableIdx = editableWeekIndexByKey.get(String(ew))
      return editableIdx === undefined ? 0 : (Array.isArray(draft?.mdPlan) ? Number(draft.mdPlan[editableIdx] ?? 0) : 0)
    })
    const draftInv   = epochWeeks.map((_, idx) => {
      const editableIdx = editableWeekIndexByKey.get(String(epochWeeks[idx]))
      if (editableIdx !== undefined && Array.isArray(draft?.invPlan) && typeof draft.invPlan[editableIdx] !== 'undefined') return Number(draft.invPlan[editableIdx] ?? 0)
      return Number(invPlanFromApi[idx] ?? 0)
    })
    const draftPlanPct = epochWeeks.map((_, idx) => (
      (() => {
        const editableIdx = editableWeekIndexByKey.get(String(epochWeeks[idx]))
        return editableIdx !== undefined && Array.isArray(draft?.planPercent) && typeof draft.planPercent[editableIdx] !== 'undefined'
          ? Number(draft.planPercent[editableIdx])
          : null
      })()
    ))
    const draftPlanSS = epochWeeks.map((_, idx) => (
      (() => {
        const editableIdx = editableWeekIndexByKey.get(String(epochWeeks[idx]))
        return editableIdx !== undefined && Array.isArray(draft?.planSS) && typeof draft.planSS[editableIdx] !== 'undefined'
          ? Number(draft.planSS[editableIdx])
          : null
      })()
    ))
    const draftMdPct = epochWeeks.map((_, idx) => (
      (() => {
        const editableIdx = editableWeekIndexByKey.get(String(epochWeeks[idx]))
        return editableIdx !== undefined && Array.isArray(draft?.mdPlanPercent) && typeof draft.mdPlanPercent[editableIdx] !== 'undefined'
          ? Number(draft.mdPlanPercent[editableIdx])
          : null
      })()
    ))

    const effectiveMonthlyRows = buildEffectiveMonthlyPlanRows({
      plannerSession,
      calcMode: plannerSession?.calcMode || calcMode || 'allocate',
    })
    // Always recompute inflows from the live plan values (salesPlan, markdownPlan, planInvBOM)
    // so that weekly inflow display reflects any edits rather than stale saved values.
    const liveMonthlyInflows = deriveMonthlyInflows(effectiveMonthlyRows)
    const effectiveMonthsByEm = new Map()
    effectiveMonthlyRows.forEach((m, idx) => {
      const em = Number(m?.epochMonth)
      if (!Number.isFinite(em)) return
      effectiveMonthsByEm.set(em, {
        ...m,
        salesPlan: Number(m?.salesPlan ?? 0),
        markdownPlan: Number(m?.markdownPlan ?? 0),
        planInvBOM: Number(m?.planInvBOM ?? 0),
        planInflowRetail: Number(liveMonthlyInflows[idx]?.planInflowRetail ?? m?.planInflowRetail ?? 0),
        planInflowCost: Number(liveMonthlyInflows[idx]?.planInflowCost ?? m?.planInflowCost ?? 0),
        onOrderRetailMtd: Number(m?.onOrderRetailMtd ?? m?.onorderretailmtd ?? 0),
        onOrderCostMtd: Number(m?.onOrderCostMtd ?? m?.onordercostmtd ?? 0),
        planPercent: Number(m?.planPercent ?? 0),
        imuPlan: Number(m?.imuPlan ?? m?.planImuPct ?? 0),
      })
    })

    const monthDayTotals = new Map()
    for (const [, mMap] of (plannerSession?.weekToMonthDayCounts || new Map()).entries()) {
      for (const [em, days] of mMap.entries()) {
        monthDayTotals.set(String(em), (monthDayTotals.get(String(em)) || 0) + Number(days || 0))
      }
    }

    const seededMonthShareWeeks = seedWeeklyPlansFromMonthlyBase({
      epochWeeks,
      monthlyBaseUi: effectiveMonthlyRows,
      weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
      weeklyActualSalesByWeek: plannerSession?.weeklyActualSalesByWeek || {},
    })

    const weeklyFallbackSales = epochWeeks.map((ew) => Number(seededMonthShareWeeks[String(ew)]?.salesPlan ?? 0))

    const weeklyFallbackMd = epochWeeks.map((ew) => Number(seededMonthShareWeeks[String(ew)]?.markdownPlan ?? 0))

    const weeklyFallbackInflowRetail = epochWeeks.map((ew) => {
      const mMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(ew)) || plannerSession?.weekToMonthDayCounts?.get?.(String(ew))
      if (!mMap) return 0
      let total = 0
      for (const [em, days] of mMap.entries()) {
        const month = effectiveMonthsByEm.get(Number(em))
        const monthDays = Number(monthDayTotals.get(String(em)) || 0)
        if (!month || !monthDays) continue
        total += Number(month.planInflowRetail || 0) * (Number(days || 0) / monthDays)
      }
      return total
    })

    const weeklyFallbackInflowCost = epochWeeks.map((_, idx) => {
      const retail = Number(weeklyFallbackInflowRetail[idx] || 0)
      const mMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(epochWeeks[idx])) || plannerSession?.weekToMonthDayCounts?.get?.(String(epochWeeks[idx]))
      if (!mMap) return retail
      let blendedImu = 0
      let totalWeight = 0
      for (const [em, days] of mMap.entries()) {
        const month = effectiveMonthsByEm.get(Number(em))
        const monthDays = Number(monthDayTotals.get(String(em)) || 0)
        if (!month || !monthDays) continue
        const w = Number(days || 0) / monthDays
        blendedImu += Number(month.imuPlan || 0) * w
        totalWeight += w
      }
      const imu = totalWeight ? blendedImu / totalWeight : 0
      return retail * (1 - toImuRate(imu))
    })

    const weeklyFallbackOnOrderRetail = epochWeeks.map((ew) => {
      const mMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(ew)) || plannerSession?.weekToMonthDayCounts?.get?.(String(ew))
      if (!mMap) return 0
      let total = 0
      for (const [em, days] of mMap.entries()) {
        const month = effectiveMonthsByEm.get(Number(em))
        const monthDays = Number(monthDayTotals.get(String(em)) || 0)
        if (!month || !monthDays) continue
        total += Number(month.onOrderRetailMtd || 0) * (Number(days || 0) / monthDays)
      }
      return total
    })

    const weeklyFallbackOnOrderCost = epochWeeks.map((ew) => {
      const mMap = plannerSession?.weekToMonthDayCounts?.get?.(Number(ew)) || plannerSession?.weekToMonthDayCounts?.get?.(String(ew))
      if (!mMap) return 0
      let total = 0
      for (const [em, days] of mMap.entries()) {
        const month = effectiveMonthsByEm.get(Number(em))
        const monthDays = Number(monthDayTotals.get(String(em)) || 0)
        if (!month || !monthDays) continue
        total += Number(month.onOrderCostMtd || 0) * (Number(days || 0) / monthDays)
      }
      return total
    })

    const weeklyFallbackInv = epochWeeks.map((ew, idx) => {
      const saved = Number(invPlanFromApi[idx] ?? 0)
      if (saved !== 0) return saved
      const dominantEpochMonth = getDominantEpochMonthForWeek({
        ew,
        weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
        fallbackEpochMonth: Number(effectiveMonthlyRows?.[0]?.epochMonth ?? 0) || null,
      })
      if (!dominantEpochMonth) return 0
      const month = effectiveMonthsByEm.get(Number(dominantEpochMonth))
      return Number(month?.planInvBOM || 0)
    })

    const weekMonthReferenceTotals = new Map(epochWeeks.map((ew) => {
      const dominantEpochMonth = getDominantEpochMonthForWeek({
        ew,
        weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts,
        fallbackEpochMonth: Number(effectiveMonthlyRows?.[0]?.epochMonth ?? 0) || null,
      })
      const month = effectiveMonthsByEm.get(Number(dominantEpochMonth)) || null
      return [String(ew), {
        salesPlan: Number(month?.salesPlan ?? 0),
        markdownPlan: Number(month?.markdownPlan ?? 0),
      }]
    }))

    // Use draft if it has real values; otherwise fall back to render-time seed from monthlyBaseUi.
    const hasDraftValues =
      draftSales.some((v) => Number(v || 0) !== 0) ||
      draftMd.some((v) => Number(v || 0) !== 0) ||
      draftPlanPct.some((v) => v !== null && typeof v !== 'undefined' && !Number.isNaN(v)) ||
      draftPlanSS.some((v) => v !== null && typeof v !== 'undefined' && !Number.isNaN(v)) ||
      draftMdPct.some((v) => v !== null && typeof v !== 'undefined' && !Number.isNaN(v))
    let salesPlanSeries = draftSales
    let mdPlanSeries    = draftMd
    let invPlanSeries   = draftInv
    let planPercentSeries = draftPlanPct
    let planSSSeries = draftPlanSS
    let mdPercentSeries = draftMdPct
    const hasMonthlyOverrides = Object.keys(plannerSession?.monthlyEditsByMonth || {}).length > 0
    const hasWeeklyOverrides = Object.keys(plannerSession?.weeklyEditsByWeek || {}).length > 0
    const hasDailyCanonical = !!(plannerSession?.dailyByDate && typeof plannerSession.dailyByDate.values === 'function' && plannerSession?.datesByWeek && typeof plannerSession.datesByWeek.get === 'function')
    const hasUnsavedDailyDraft = !!(plannerSession?.dailyByDate && plannerSession?.dailyIndex?.byDate && plannerSession.dailyByDate !== plannerSession.dailyIndex.byDate)
    if (hasDailyCanonical && (hasUnsavedDailyDraft || hasMonthlyOverrides || hasWeeklyOverrides)) {
      const rolled = rollupDailyToWeeks({ byDate: plannerSession.dailyByDate, epochWeeks })
      salesPlanSeries = epochWeeks.map((ew) => Number(rolled?.[String(ew)]?.salesPlan ?? 0))
      mdPlanSeries = epochWeeks.map((ew) => Number(rolled?.[String(ew)]?.markdownPlan ?? 0))
      invPlanSeries = epochWeeks.map((ew, idx) => {
        // Only prefer the in-session draft BOW over the live daily-canonical
        // when the user has *explicitly* changed the value in this session.
        // We detect that by comparing draft vs. the raw API-loaded baseline
        // (invPlanFromApi): if they differ, the user must have edited it.
        //
        // This is critical for split-week segment edits: those updates write to
        // a mid-week date (e.g. April 1st), NOT to dates[0] (e.g. March 31st).
        // Without this check, dates[0]'s stale inventory would always win and
        // the recalculation would never be visible.
        //
        // We intentionally do NOT use this override when draft === rawApiBow
        // (initial API state), which was the bug: draft.invPlan is pre-loaded
        // from the API at startup, so `typeof draft.invPlan[idx] !== 'undefined'`
        // was always true and the draft shadowed the live daily value for ALL weeks.
        const editableIdx = editableWeekIndexByKey.get(String(ew))
        if (editableIdx !== undefined && Array.isArray(draft?.invPlan) && typeof draft.invPlan[editableIdx] !== 'undefined') {
          const draftBow = Number(draft.invPlan[editableIdx] ?? 0)
          const rawApiBow = Number(invPlanFromApi[idx] ?? 0)
          if (draftBow !== rawApiBow && draftBow !== 0) return draftBow
        }
        const dates = plannerSession.datesByWeek.get(String(ew)) || []
        const first = dates.length ? plannerSession.dailyByDate.get(dates[0]) : null
        const inv = Number(first?.inventory ?? 0)
        if (inv !== 0) return inv
        // When the daily record exists but inventory is 0 the next checks apply;
        // when there is NO daily coverage for this week, fall back to the local
        // draft so that planInvBOM edits are immediately visible even on weeks
        // that sit outside the daily-canonical date range.
        const draftBow = draftInv[idx]
        if (draftBow !== null && typeof draftBow !== 'undefined' && Number(draftBow) !== 0) return Number(draftBow)
        const savedInv = Number(invPlanFromApi[idx] ?? 0)
        if (savedInv !== 0) return savedInv
        const fallbackInv = Number(weeklyFallbackInv[idx] ?? 0)
        if (fallbackInv !== 0) return fallbackInv
        const actualInv = Number(weeklyInvActual[idx] ?? 0)
        return actualInv !== 0 ? actualInv : 0
      })
      salesPlanSeries = salesPlanSeries.map((v, idx) => (Number(v || 0) !== 0 ? Number(v || 0) : Number(weeklyFallbackSales[idx] || 0)))
      mdPlanSeries = mdPlanSeries.map((v, idx) => (Number(v || 0) !== 0 ? Number(v || 0) : Number(weeklyFallbackMd[idx] || 0)))
      planPercentSeries = epochWeeks.map((ew, idx) => deriveWeeklyPlanPercentDisplay({
        actualSales: salesActual[idx],
        salesPlan: salesPlanSeries[idx],
        preferredPlanPercent: draftPlanPct[idx] ?? seededMonthShareWeeks[String(ew)]?.planPercent,
        monthSalesTotal: Number(weekMonthReferenceTotals.get(String(ew))?.salesPlan ?? 0),
      }))
      planSSSeries = epochWeeks.map((_, idx) => {
        const sales = Number(salesPlanSeries[idx] ?? 0)
        const inv = Number(invPlanSeries[idx] ?? 0)
        return sales ? (inv / sales) : 0
      })
      mdPercentSeries = epochWeeks.map((_, idx) => {
        return deriveWeeklyMonthPercentDisplay({
          periodValue: mdPlanSeries[idx],
          preferredPercent: draftMdPct[idx] ?? seededMonthShareWeeks[String(epochWeeks[idx])]?.markdownPlanPercent,
          monthTotal: Number(weekMonthReferenceTotals.get(String(epochWeeks[idx]))?.markdownPlan ?? 0),
        })
      })
    } else if (!hasDraftValues) {
      const base = Array.isArray(plannerSession?.monthlyBaseUi) ? plannerSession.monthlyBaseUi : []
      if (base.length > 0) {
        const getI = (m) => { const v = m?.planInvBOM ?? m?.inventory_bom_plan ?? m?.inventoryBomPlan ?? m?.PlanInvBOM ?? 0; return Number(v) || 0 }
        const firstMonth = base[0]
        const mi = getI(firstMonth)
        salesPlanSeries = epochWeeks.map((ew) => Number(seededMonthShareWeeks[String(ew)]?.salesPlan ?? 0))
        mdPlanSeries = epochWeeks.map((ew) => Number(seededMonthShareWeeks[String(ew)]?.markdownPlan ?? 0))
        invPlanSeries = epochWeeks.map((_, idx) => {
          const saved = Number(invPlanFromApi[idx] ?? 0)
          if (saved !== 0) return saved
          const fallbackInv = Number(weeklyFallbackInv[idx] ?? 0)
          if (fallbackInv !== 0) return fallbackInv
          const a = Number(weeklyInvActual[idx] ?? 0)
          return a !== 0 ? a : mi
        })
        planPercentSeries = epochWeeks.map((ew, idx) => {
          const seededPct = seededMonthShareWeeks[String(ew)]?.planPercent
          return deriveWeeklyPlanPercentDisplay({
            actualSales: salesActual[idx],
            salesPlan: salesPlanSeries[idx],
            preferredPlanPercent: seededPct,
            monthSalesTotal: Number(weekMonthReferenceTotals.get(String(ew))?.salesPlan ?? 0),
          })
        })
        planSSSeries = epochWeeks.map((_, idx) => {
          const sales = Number(salesPlanSeries[idx] ?? 0)
          const inv = Number(invPlanSeries[idx] ?? 0)
          return sales ? (inv / sales) : 0
        })
        mdPercentSeries = epochWeeks.map((ew, idx) => {
          return deriveWeeklyMonthPercentDisplay({
            periodValue: mdPlanSeries[idx],
            preferredPercent: seededMonthShareWeeks[String(ew)]?.markdownPlanPercent,
            monthTotal: Number(weekMonthReferenceTotals.get(String(ew))?.markdownPlan ?? 0),
          })
        })
      }
    } else {
      planPercentSeries = epochWeeks.map((ew, idx) => deriveWeeklyPlanPercentDisplay({
        actualSales: salesActual[idx],
        salesPlan: salesPlanSeries[idx],
        preferredPlanPercent: draftPlanPct[idx] ?? seededMonthShareWeeks[String(ew)]?.planPercent,
        monthSalesTotal: Number(weekMonthReferenceTotals.get(String(ew))?.salesPlan ?? 0),
      }))
      planSSSeries = epochWeeks.map((_, idx) => {
        const draftVal = draftPlanSS[idx]
        if (draftVal !== null && !Number.isNaN(draftVal)) return Number(draftVal)
        const sales = Number(salesPlanSeries[idx] ?? 0)
        const inv = Number(invPlanSeries[idx] ?? 0)
        return sales ? (inv / sales) : 0
      })
      mdPercentSeries = epochWeeks.map((_, idx) => {
        const draftVal = draftMdPct[idx]
        if (draftVal !== null && !Number.isNaN(draftVal)) return Number(draftVal)
        return deriveWeeklyMonthPercentDisplay({
          periodValue: mdPlanSeries[idx],
          monthTotal: Number(weekMonthReferenceTotals.get(String(epochWeeks[idx]))?.markdownPlan ?? 0),
        })
      })
    }
    if (rearHistoricalWeekSet.size && hasDailyCanonical) {
      const rearEpochWeeks = epochWeeks.filter((week) => rearHistoricalWeekSet.has(String(week)))
      const rearRolled = rollupDailyToWeeks({ byDate: plannerSession.dailyByDate, epochWeeks: rearEpochWeeks })

      epochWeeks.forEach((ew, idx) => {
        if (!rearHistoricalWeekSet.has(String(ew))) return
        const rolledWeek = rearRolled?.[String(ew)] || {}
        const weekDates = plannerSession?.datesByWeek?.get?.(String(ew)) || []
        const firstDate = Array.isArray(weekDates) && weekDates.length ? weekDates[0] : null
        salesPlanSeries[idx] = Number(rolledWeek?.salesPlan ?? salesPlanSeries[idx] ?? 0)
        mdPlanSeries[idx] = Number(rolledWeek?.markdownPlan ?? mdPlanSeries[idx] ?? 0)
        if (firstDate) {
          invPlanSeries[idx] = Number(plannerSession?.dailyByDate?.get?.(firstDate)?.inventory ?? invPlanSeries[idx] ?? 0)
        }
        planPercentSeries[idx] = deriveWeeklyPlanPercentDisplay({
          actualSales: salesActual[idx],
          salesPlan: salesPlanSeries[idx],
          monthSalesTotal: Number(weekMonthReferenceTotals.get(String(ew))?.salesPlan ?? 0),
        })
        planSSSeries[idx] = Number(salesPlanSeries[idx] ?? 0) ? (Number(invPlanSeries[idx] ?? 0) / Number(salesPlanSeries[idx] ?? 0)) : 0
        mdPercentSeries[idx] = deriveWeeklyMonthPercentDisplay({
          periodValue: mdPlanSeries[idx],
          monthTotal: Number(weekMonthReferenceTotals.get(String(ew))?.markdownPlan ?? 0),
        })
      })
    }

    // Tail weeks are beyond the editable window but complete the last visible month.
    // They are always read-only and use the seeded month-share allocation (never user-editable).
    if (tailWeekSet.size) {
      epochWeeks.forEach((ew, idx) => {
        if (!tailWeekSet.has(String(ew))) return
        salesPlanSeries[idx] = Number(weeklyFallbackSales[idx] ?? 0)
        mdPlanSeries[idx] = Number(weeklyFallbackMd[idx] ?? 0)
        if (!invPlanSeries[idx]) {
          invPlanSeries[idx] = Number(weeklyFallbackInv[idx] ?? 0)
        }
        planPercentSeries[idx] = deriveWeeklyPlanPercentDisplay({
          actualSales: salesActual[idx],
          salesPlan: salesPlanSeries[idx],
          monthSalesTotal: Number(weekMonthReferenceTotals.get(String(ew))?.salesPlan ?? 0),
        })
        planSSSeries[idx] = Number(salesPlanSeries[idx] ?? 0) ? (Number(invPlanSeries[idx] ?? 0) / Number(salesPlanSeries[idx] ?? 0)) : 0
        mdPercentSeries[idx] = deriveWeeklyMonthPercentDisplay({
          periodValue: mdPlanSeries[idx],
          monthTotal: Number(weekMonthReferenceTotals.get(String(ew))?.markdownPlan ?? 0),
        })
      })
    }

    const inflowRetailPlan = csvToNums(row?.InflowRetailPlanL12W)
    const inflowRetailPlanByWeek = new Map(editableEpochWeeks.map((week, idx) => [String(week), Number(inflowRetailPlan[idx] ?? 0)]))
    const trendedTurnRaw = csvToNums(
      row?.TrendedTurnL12W ?? row?.TrendedStockToSalesL12W ?? row?.WeeklyTrendedTurnL12W
    )
    const rawTrendedStockToSalesByWeek = new Map(editableEpochWeeks.map((week, idx) => [String(week), trendedTurnRaw[idx] ?? null]))
    const maxLen = Math.max(
      12,
      epochWeeks.length,
      salesActual.length,
      mdActual.length,
      invActual.length,
      salesPlanSeries.length,
      mdPlanSeries.length,
      invPlanSeries.length,
      trendedTurnRaw.length,
      inflowRetailPlan.length
    )
    const pad = (arr) => {
      const out = (arr || []).slice(0, maxLen)
      while (out.length < maxLen) out.push(0)
      return out
    }
    const padNullable = (arr) => {
      const out = (arr || []).slice(0, maxLen)
      while (out.length < maxLen) out.push(null)
      return out
    }
    const ew = epochWeeks.length ? epochWeeks : Array.from({ length: maxLen }, (_, i) => String(i + 1))
    const currentEpochWeek = editableEpochWeeks.length
      ? String(editableEpochWeeks[0])
      : (row?.epoch_week ? String(row.epoch_week) : (epochWeek ? String(epochWeek) : (ew.length ? String(ew[0]) : '')))
    const sa  = pad(salesActual)
    const mda = pad(mdActual)
    const iva = pad(invActual)
    const sp  = pad(salesPlanSeries)
    const mdp = pad(mdPlanSeries)
    const ivp = pad(invPlanSeries)
    const rawTrendedStockToSalesSeries = padNullable(epochWeeks.map((ew) => rawTrendedStockToSalesByWeek.get(String(ew)) ?? null))
    // When live plan edits exist (K-Flow / manual BOM changes), always prefer the recalculated
    // monthly-derived fallback over the stale saved weekly API value — even when it is 0.
    const hasLivePlanEdits = hasMonthlyOverrides || hasWeeklyOverrides || hasUnsavedDailyDraft
    const derivedWeeklyInflowRetail = deriveWeeklyInflowsFromPlanSeries({
      salesPlanSeries,
      markdownPlanSeries: mdPlanSeries,
      invPlanSeries,
      fallbackInflowRetailSeries: weeklyFallbackInflowRetail,
    })
    const irp = pad(epochWeeks.map((_, idx) => {
      const apiVal = Number(inflowRetailPlanByWeek.get(String(epochWeeks[idx])) ?? 0)
      const fallbackVal = Number(weeklyFallbackInflowRetail[idx] ?? 0)
      return (hasLivePlanEdits || fallbackVal !== 0) ? Number(derivedWeeklyInflowRetail[idx] ?? fallbackVal) : apiVal
    }))
    const plannedImuPct = Number(row?.imu_plan ?? 0)
    const inflowCost = irp.map((v, idx) => {
      const fallbackVal = Number(weeklyFallbackInflowCost[idx] ?? 0)
      if (hasLivePlanEdits || fallbackVal !== 0) {
        const fallbackRetail = Number(weeklyFallbackInflowRetail[idx] ?? 0)
        const retainedRatio = fallbackRetail !== 0 ? (fallbackVal / fallbackRetail) : (1 - toImuRate(plannedImuPct))
        return Number(v || 0) * retainedRatio
      }
      return Number(v || 0) * (1 - toImuRate(plannedImuPct))
    })
    const onOrderRetail = pad(rollForwardValuesToCurrentWeek({
      epochWeeks,
      currentEpochWeek,
      values: epochWeeks.map((_, idx) => Number(weeklyFallbackOnOrderRetail[idx] ?? 0)),
    }))
    const onOrderCost = pad(rollForwardValuesToCurrentWeek({
      epochWeeks,
      currentEpochWeek,
      values: epochWeeks.map((_, idx) => Number(weeklyFallbackOnOrderCost[idx] ?? 0)),
    }))
    const rows = ew.slice(0, maxLen).map((w, i) => {
      const isCurrent = String(w) === String(currentEpochWeek)
      const isFuture  = Number(w) > Number(currentEpochWeek)
      const monthMeta = representativeMonthMetaForWeek(w)
      const representativeMonth = effectiveMonthsByEm.get(Number(monthMeta.representativeEpochMonth)) || null
      const salesPlanVal  = Number(sp[i]  ?? 0)
      const salesActualVal = Number(sa[i] ?? 0)
      const mdPlanVal     = Math.round(Number(mdp[i] ?? 0))
      const mdActualVal   = Number(mda[i] ?? 0)
      const planPctDefault = Number(planPercentSeries[i] ?? (salesActualVal ? (salesPlanVal / salesActualVal) * 100 : 0))
      const mdPctDefault   = Number(mdPercentSeries[i] ?? 0)
      const actualSSVal = salesActualVal ? (Number(iva[i] ?? 0) / salesActualVal) : 0
      const planSSVal = Number(planSSSeries[i] ?? (salesPlanVal ? (Number(ivp[i] ?? 0) / salesPlanVal) : 0))
      const rawTrendedStockToSales = rawTrendedStockToSalesSeries[i]
      const trendedTurn = Number(rawTrendedStockToSales) > 0 ? (52 / Number(rawTrendedStockToSales)) : null
      const freshness = i >= 3
        ? calculateWeeklyFreshnessFactor({
          bowInventory: Number(iva[i] ?? 0) > 0 ? Number(iva[i] ?? 0) : Number(ivp[i] ?? 0),
          priorThreeWeekInflows: irp.slice(Math.max(0, i - 3), i),
        })
        : null
       const monthSegments = buildMonthSegmentsForWeek({
         ew: w,
         salesPlanVal,
         mdPlanVal,
         planInvBOMVal: Number(ivp[i] ?? 0),
         actualInvBOMVal: Number(iva[i] ?? 0),
          onOrderRetailVal: Number(onOrderRetail[i] ?? 0),
          onOrderCostVal: Number(onOrderCost[i] ?? 0),
       })
      return {
        period: String(w),
        shortPeriod: `EW${w}`,
        headerLabel: headerWeekLabelForWeek(w),
        representativeMonthKey: monthMeta.representativeMonthKey,
        representativeMonthLabel: monthMeta.representativeMonthLabel,
        representativeMonthDayCount: monthMeta.representativeMonthDayCount,
        isPartialWeekInMonth: monthMeta.isPartialWeekInMonth,
        salesActual: salesActualVal,
        salesPlan: salesPlanVal,
        salesActualMonthlyReference: monthMeta.representativeEpochMonth
          ? Number(monthlyActualSalesByEm.get(Number(monthMeta.representativeEpochMonth)) ?? 0)
          : null,
        salesPlanMonthlyReference: representativeMonth ? Number(representativeMonth?.salesPlan ?? 0) : null,
        planPercent: planPctDefault,
        actualPercent: 0,
        actualInvBOM: Number(iva[i] ?? 0),
        actualSS: actualSSVal,
        freshness,
        rawTrendedStockToSales,
        trendedTurn,
        planSS: planSSVal,
        planInvBOM: Number(ivp[i] ?? 0),
        planInvBOMMonthlyReference: representativeMonth ? Number(representativeMonth?.planInvBOM ?? 0) : null,
        planInflowRetail: Number(irp[i] ?? 0),
        planInflowCost: Number(inflowCost[i] ?? 0),
        onOrderRetailMtd: Number(onOrderRetail[i] ?? 0),
        onOrderCostMtd: Number(onOrderCost[i] ?? 0),
        actualInflowRetail: Number(inflowRetailActualByWeek[i] ?? 0),
        actualInflowCost: Number(inflowCostActualByWeek[i] ?? 0),
        markdownActual: mdActualVal,
        markdownActualMonthlyReference: monthMeta.representativeEpochMonth
          ? Number(monthlyActualMdByEm.get(Number(monthMeta.representativeEpochMonth)) ?? 0)
          : null,
        markdownPlan: mdPlanVal,
        markdownPlanMonthlyReference: representativeMonth ? Number(representativeMonth?.markdownPlan ?? 0) : null,
        markdownPlanPercent: mdPctDefault,
        markdownActualPercent: 0,
         monthSegments,
         isReadOnlyPeriod: rearHistoricalWeekSet.has(String(w)) || tailWeekSet.has(String(w)),
         isRearHistoricalPeriod: rearHistoricalWeekSet.has(String(w)),
         isTailWeek: tailWeekSet.has(String(w)),
        isCurrentPeriod: isCurrent,
        isFuturePeriod: isFuture,
      }
    })

    const monthTotalsByKey = new Map()
    rows.forEach((row) => {
      const rowSegments = Array.isArray(row?.monthSegments) ? row.monthSegments : []
      if (rowSegments.length > 1) {
        rowSegments.forEach((segment) => {
          const monthKey = String(segment?.monthKey || row?.representativeMonthKey || '')
          if (!monthKey) return
          if (!monthTotalsByKey.has(monthKey)) monthTotalsByKey.set(monthKey, { salesPlan: 0, salesActual: 0, markdownPlan: 0, markdownActual: 0 })
          const current = monthTotalsByKey.get(monthKey)
          const rawTotal = rowSegments.reduce((acc, currentSegment) => acc + Number(currentSegment?.days || 0), 0)
          const share = rawTotal > 0 ? (Number(segment?.days || 0) / rawTotal) : (1 / rowSegments.length)
          current.salesPlan += Number(segment?.salesPlan ?? Number(row?.salesPlan || 0) * share)
          current.salesActual += Number(segment?.salesActual ?? Number(row?.salesActual || 0) * share)
          current.markdownPlan += Number(segment?.markdownPlan ?? Number(row?.markdownPlan || 0) * share)
          current.markdownActual += Number(segment?.markdownActual ?? Number(row?.markdownActual || 0) * share)
        })
        return
      }

      const monthKey = String(row?.representativeMonthKey || '')
      if (!monthKey) return
      if (!monthTotalsByKey.has(monthKey)) monthTotalsByKey.set(monthKey, { salesPlan: 0, salesActual: 0, markdownPlan: 0, markdownActual: 0 })
      const current = monthTotalsByKey.get(monthKey)
      current.salesPlan += Number(row?.salesPlan || 0)
      current.salesActual += Number(row?.salesActual || 0)
      current.markdownPlan += Number(row?.markdownPlan || 0)
      current.markdownActual += Number(row?.markdownActual || 0)
    })

    return rows.map((row) => ({
      ...row,
      planPercent: (() => {
        const monthTotals = monthTotalsByKey.get(String(row?.representativeMonthKey || '')) || null
        return Number(monthTotals?.salesPlan || 0) > 0 ? (Number(row?.salesPlan || 0) / Number(monthTotals.salesPlan || 0)) * 100 : Number(row?.planPercent || 0)
      })(),
      actualPercent: (() => {
        const monthTotals = monthTotalsByKey.get(String(row?.representativeMonthKey || '')) || null
        return Number(monthTotals?.salesActual || 0) > 0 ? (Number(row?.salesActual || 0) / Number(monthTotals.salesActual || 0)) * 100 : 0
      })(),
      markdownPlanPercent: (() => {
        const monthTotals = monthTotalsByKey.get(String(row?.representativeMonthKey || '')) || null
        return Number(monthTotals?.markdownPlan || 0) > 0 ? (Number(row?.markdownPlan || 0) / Number(monthTotals.markdownPlan || 0)) * 100 : Number(row?.markdownPlanPercent || 0)
      })(),
      markdownActualPercent: (() => {
        const monthTotals = monthTotalsByKey.get(String(row?.representativeMonthKey || '')) || null
        return Number(monthTotals?.markdownActual || 0) > 0 ? (Number(row?.markdownActual || 0) / Number(monthTotals.markdownActual || 0)) * 100 : 0
      })(),
    }))
  }

  const { periodData, summaryData } = useMemo(() => {
    if (!apiRow) return { periodData: [], summaryData: null }
    const pd = shapePeriods(apiRow)

    return { periodData: pd, summaryData: buildWeeklySummary(pd) }
  }, [apiRow, draft, plannerSession?.dailyByDate, plannerSession?.datesByWeek, plannerSession?.dailyIndex, plannerSession?.monthlyEditsByMonth, plannerSession?.weeklyEditsByWeek, plannerSession?.monthlyBaseUi, plannerSession?.weekToMonthDayCounts])

  const headerAnnualValues = useMemo(() => {
    const monthlyActualRows = Array.isArray(plannerSession?.monthlyBase) ? plannerSession.monthlyBase : []
    const livePlanRows = buildEffectiveMonthlyPlanRows({
      plannerSession,
      calcMode: plannerSession?.calcMode || calcMode || 'allocate',
    })

    const inflows = deriveMonthlyInflows(livePlanRows)
    const rowsWithInflows = livePlanRows.map((row, idx) => ({
      ...row,
      planInflowRetail: Number(inflows[idx]?.planInflowRetail || 0),
      planInflowCost: Number(inflows[idx]?.planInflowCost || 0),
    }))

    const baseAnnualValues = buildPlannerAnnualValues({
      planRows: rowsWithInflows,
      actualRows: monthlyActualRows,
    })

    return {
      ...baseAnnualValues,
      trendedTurn: summaryData?.values?.trendedTurn ?? null,
    }
  }, [plannerSession?.monthlyBaseUi, plannerSession?.monthlyBase, plannerSession?.monthlyEditsByMonth, plannerSession?.weeklyEditsByWeek, plannerSession?.weekToMonthDayCounts, plannerSession?.weeklyActualSalesByWeek, plannerSession?.dailyByDate, plannerSession?.datesByMonth, plannerSession?.calcMode, calcMode, summaryData])

  const handleCopyActualsToPlan = (kind) => {
    const ps = plannerSessionRef.current
    if (!ps?.dailyByDate || typeof ps.dailyByDate.get !== 'function' || !ps?.datesByWeek || typeof ps.datesByWeek.get !== 'function') {
      messageContext.show('Planner daily context is not ready yet')
      return
    }

    const rows = Array.isArray(periodData) ? periodData : []
    if (!rows.length) return

    let nextDaily = ps.dailyByDate
    const totalPlanSales = rows.reduce((acc, row) => acc + Number(row?.salesPlan || 0), 0)
    const totalPlanMarkdown = rows.reduce((acc, row) => acc + Number(row?.markdownPlan || 0), 0)
    const totalActualSales = rows.reduce((acc, row) => acc + Number(row?.salesActual || 0), 0)
    const totalActualMarkdown = rows.reduce((acc, row) => acc + Number(row?.markdownActual || 0), 0)

    if (kind === 'sales' && totalPlanSales > 0 && totalActualSales > 0) {
      rows.forEach((row) => {
        const dates = ps.datesByWeek.get(String(row.period)) || []
        if (!dates.length) return
        const desiredSales = totalPlanSales * (Number(row?.salesActual || 0) / totalActualSales)
        nextDaily = applyEditAggregate({ byDate: nextDaily, dates, field: 'salesPlan', newTotal: Math.max(0, desiredSales), clampMin: 0 })
        const mdRate = Number(row?.salesPlan || 0) ? (Number(row?.markdownPlan || 0) / Number(row?.salesPlan || 0)) : 0
        nextDaily = applyEditAggregate({ byDate: nextDaily, dates, field: 'markdownPlan', newTotal: Math.max(0, desiredSales * mdRate), clampMin: 0 })
        nextDaily = applyFirstDayInventory(nextDaily, dates, desiredSales * Number(row?.planSS || 0))
      })
      commitDailyMutation(nextDaily)
      return
    }

    if (kind === 'inventory') {
      rows.forEach((row) => {
        const dates = ps.datesByWeek.get(String(row.period)) || []
        if (!dates.length) return
        nextDaily = applyFirstDayInventory(nextDaily, dates, Number(row?.salesPlan || 0) * Number(row?.actualSS || 0))
      })
      commitDailyMutation(nextDaily)
      return
    }

    if (kind === 'markdown' && totalPlanMarkdown > 0 && totalActualMarkdown > 0) {
      rows.forEach((row) => {
        const dates = ps.datesByWeek.get(String(row.period)) || []
        if (!dates.length) return
        const desiredMd = totalPlanMarkdown * (Number(row?.markdownActual || 0) / totalActualMarkdown)
        nextDaily = applyEditAggregate({ byDate: nextDaily, dates, field: 'markdownPlan', newTotal: Math.max(0, desiredMd), clampMin: 0 })
      })
      commitDailyMutation(nextDaily)
    }
  }

   const handleCellChange = (weekIndex, field, value, segmentMeta = null) => {
    // eslint-disable-next-line no-console
    console.log('[planner][handleCellChange]', { weekIndex, field, value, segmentMeta })
    if (!['salesPlan', 'markdownPlan', 'planInvBOM', 'planPercent', 'markdownPlanPercent'].includes(field)) return

    const nextVal = Number(value)

    const ps = plannerSessionRef.current
    const hasDaily = ps?.dailyByDate && typeof ps.dailyByDate?.get === 'function' && ps?.datesByWeek && typeof ps.datesByWeek?.get === 'function'
    const activeCalcMode = ps?.calcMode || calcMode || 'allocate'
    const editableEpochWeeks = getDisplayEpochWeeks(apiRow)
    const currentVisibleRow = Array.isArray(periodData) ? (periodData?.[weekIndex] || {}) : {}
    const ew = currentVisibleRow?.period ? String(currentVisibleRow.period) : editableEpochWeeks?.[weekIndex]
    const editableWeekIndex = editableEpochWeeks.findIndex((week) => String(week) === String(ew))
    if (currentVisibleRow?.isReadOnlyPeriod || editableWeekIndex === -1) return
    const dates = ew ? (ps?.datesByWeek?.get(String(ew)) || []) : []
    const segmentIndex = Number.isInteger(segmentMeta?.segmentIndex) ? Number(segmentMeta.segmentIndex) : null
    const currentVisibleSegment = segmentIndex !== null ? (currentVisibleRow?.monthSegments?.[segmentIndex] || null) : null
    const editDates = currentVisibleSegment?.dates?.length ? currentVisibleSegment.dates : dates
    const editedEpochMonths = currentVisibleSegment?.monthKey
      ? [String(currentVisibleSegment.monthKey)]
      : Array.from(new Set((editDates || []).map((dt) => String(ps?.dailyByDate?.get(dt)?.epochMonth || '')).filter(Boolean)))
    const targetEpochMonth = String(currentVisibleSegment?.monthKey || editedEpochMonths?.[0] || currentVisibleRow?.representativeMonthKey || '')

    const effectiveMonthlyRows = buildEffectiveMonthlyPlanRows({
      plannerSession: ps,
      calcMode: ps?.calcMode || calcMode || 'allocate',
    })
    const monthSalesTotal = Number(
      effectiveMonthlyRows.find((month) => String(month?.epochMonth) === targetEpochMonth)?.salesPlan
      ?? currentVisibleSegment?.salesPlanMonthlyReference
      ?? currentVisibleRow?.salesPlanMonthlyReference
      ?? 0
    )
    const monthMarkdownTotal = Number(
      effectiveMonthlyRows.find((month) => String(month?.epochMonth) === targetEpochMonth)?.markdownPlan
      ?? currentVisibleSegment?.markdownPlanMonthlyReference
      ?? currentVisibleRow?.markdownPlanMonthlyReference
      ?? 0
    )

    const currentSalesTotal = currentVisibleSegment
      ? Number(currentVisibleSegment?.salesPlan ?? 0)
      : dates.length
      ? dates.reduce((acc, dt) => acc + Number(ps?.dailyByDate?.get(dt)?.salesPlan || 0), 0)
      : Number(currentVisibleRow?.salesPlan || 0)
    const currentMdTotal = currentVisibleSegment
      ? Number(currentVisibleSegment?.markdownPlan ?? 0)
      : dates.length
      ? dates.reduce((acc, dt) => acc + Number(ps?.dailyByDate?.get(dt)?.markdownPlan || 0), 0)
      : Number(currentVisibleRow?.markdownPlan || 0)
    const currentInvBow = currentVisibleSegment
      ? Number(currentVisibleSegment?.planInvBOM ?? 0)
      : dates.length
      ? Number(ps?.dailyByDate?.get(dates[0])?.inventory || 0)
      : Number(currentVisibleRow?.planInvBOM || 0)
    const actualSales = Number(ps?.weeklyActualSalesByWeek?.[String(ew)] ?? 0)
    const currentPlanPercent = deriveWeeklyPlanPercentDisplay({
      actualSales,
      salesPlan: currentSalesTotal,
      monthSalesTotal,
      preferredPlanPercent: currentVisibleSegment?.planPercent ?? currentVisibleRow?.planPercent ?? null,
    })
    const currentMarkdownPlanPercent = deriveWeeklyMonthPercentDisplay({
      periodValue: currentMdTotal,
      monthTotal: monthMarkdownTotal,
      preferredPercent: currentVisibleSegment?.markdownPlanPercent ?? currentVisibleRow?.markdownPlanPercent ?? null,
    })

    const desiredSalesTotal = (() => {
      if (field === 'salesPlan') return Math.max(0, nextVal)
      if (field === 'planPercent') {
        return deriveWeeklySalesTotalFromPlanPercent({
          nextPlanPercent: nextVal,
          monthSalesTotal,
          actualSales,
          currentSalesTotal,
          currentPlanPercent,
        })
      }
      return currentSalesTotal
    })()

    const desiredMarkdownTotal = (() => {
      if (field === 'markdownPlan' ) return Math.max(0, Math.round(nextVal))
      if (field === 'markdownPlanPercent') {
        return Math.round(deriveWeeklyTotalFromMonthPercent({
          nextPercent: nextVal,
          monthTotal: monthMarkdownTotal,
          currentTotal: currentMdTotal,
          currentPercent: currentMarkdownPlanPercent,
        }))
      }
      return currentMdTotal
    })()

    const desiredInvBow = (() => {
      if (field === 'planInvBOM' ) return Math.max(0, nextVal)
      return currentInvBow
    })()

    const resolvedScopeDates = currentVisibleSegment ? editDates : dates

    let nextDaily = ps?.dailyByDate

    if (hasDaily && editDates.length) {
      nextDaily = ps.dailyByDate

      if (field === 'salesPlan' || field === 'planPercent') {
        if (activeCalcMode === 'allocate' && ps?.datesByMonth) {
          nextDaily = applyEditAllocateMonthLocked({
            byDate: nextDaily,
            datesEdited: editDates,
            field: 'salesPlan',
            newTotal: desiredSalesTotal,
            datesByMonth: ps.datesByMonth,
            editedEpochMonths,
            clampMin: 0,
          })
        } else {
          nextDaily = applyEditAggregate({
            byDate: nextDaily,
            dates: editDates,
            field: 'salesPlan',
            newTotal: desiredSalesTotal,
            clampMin: 0,
          })
        }
      }

      if (field === 'markdownPlan' || field === 'markdownPlanPercent') {
        if (activeCalcMode === 'allocate' && ps?.datesByMonth) {
          nextDaily = applyEditAllocateMonthLocked({
            byDate: nextDaily,
            datesEdited: editDates,
            field: 'markdownPlan',
            newTotal: desiredMarkdownTotal,
            datesByMonth: ps.datesByMonth,
            editedEpochMonths,
            clampMin: 0,
          })
        } else {
          nextDaily = applyEditAggregate({
            byDate: nextDaily,
            dates: editDates,
            field: 'markdownPlan',
            newTotal: desiredMarkdownTotal,
            clampMin: 0,
          })
        }
      }

      if (field === 'planInvBOM') {
        const first = editDates[0]
        const nextMap = new Map(nextDaily)
        const rec = nextMap.get(first)
        if (rec) {
          nextMap.set(first, { ...rec, inventory: desiredInvBow })
        } else {
          // Synthesise a minimal stub record so the BOW edit is reflected in the
          // daily-canonical rendering path even when the daily index doesn't cover
          // this particular week's boundary date.
          nextMap.set(first, {
            date: first,
            inventory: desiredInvBow,
            salesPlan: 0,
            markdownPlan: 0,
            epochWeek: Number(ew) || null,
            epochMonth: null,
          })
        }
        // Always assign the updated map for planInvBOM edits so the caller
        // receives the new BOW value regardless of whether a record pre-existed.
        nextDaily = nextMap
      }

      if (nextDaily && typeof onDailyByDateChange === 'function') {
        onDailyByDateChange(nextDaily)
      }
    }

    const nextWeeklySalesTotal = resolvedScopeDates.length && nextDaily
      ? resolvedScopeDates.reduce((acc, dt) => acc + Number(nextDaily?.get(dt)?.salesPlan || 0), 0)
      : (field === 'salesPlan' || field === 'planPercent'
        ? desiredSalesTotal
        : Number((currentVisibleSegment?.salesPlan ?? currentVisibleRow?.salesPlan) || 0))
    const nextWeeklyMdTotal = resolvedScopeDates.length && nextDaily
      ? Math.round(resolvedScopeDates.reduce((acc, dt) => acc + Number(nextDaily?.get(dt)?.markdownPlan || 0), 0))
      : (field === 'markdownPlan' || field === 'markdownPlanPercent'
        ? desiredMarkdownTotal
        : Math.round(Number((currentVisibleSegment?.markdownPlan ?? currentVisibleRow?.markdownPlan) || 0)))
    // For planInvBOM edits, always use the explicitly desired value to avoid the
    // case where nextDaily.get(firstDate)?.inventory falls back to 0 (e.g. when
    // the stub record above has a zero-valued inventory or the date is missing).
    const nextWeeklyInvBow = field === 'planInvBOM'
      ? desiredInvBow
      : (resolvedScopeDates.length && nextDaily
        ? Number(nextDaily?.get(resolvedScopeDates[0])?.inventory || 0)
        : Number((currentVisibleSegment?.planInvBOM ?? currentVisibleRow?.planInvBOM) || 0))

    setDraft((prev) => {
      const next = { ...prev }

      const setAt = (key, val) => {
        const arr = [...(next[key] || [])]
        arr[editableWeekIndex] = val
        next[key] = arr
      }

      if (field === 'salesPlan' || field === 'planPercent') {
        setAt('salesPlan', nextWeeklySalesTotal)
      }
      if (field === 'planPercent') {
        setAt('planPercent', Number(nextVal))
      }
      if (field === 'markdownPlan' || field === 'markdownPlanPercent') {
        setAt('mdPlan', nextWeeklyMdTotal)
      }
      if (field === 'markdownPlanPercent') {
        setAt('mdPlanPercent', Number(nextVal))
      }
      if (field === 'planInvBOM') {
        setAt('invPlan', nextWeeklyInvBow)
      }

      return next
    })

    setDirty(true)

    if (!ew || typeof onWeeklyEdit !== 'function') return

    const patch = {
      salesPlan: nextWeeklySalesTotal,
      markdownPlan: nextWeeklyMdTotal,
      planPercent: deriveWeeklyPlanPercentDisplay({
        actualSales,
        salesPlan: nextWeeklySalesTotal,
        monthSalesTotal,
      }),
      markdownPlanPercent: deriveWeeklyMonthPercentDisplay({
        periodValue: nextWeeklyMdTotal,
        monthTotal: monthMarkdownTotal,
      }),
      planInvBOM: nextWeeklyInvBow,
      planSS: nextWeeklySalesTotal ? (nextWeeklyInvBow / nextWeeklySalesTotal) : 0,
      ...(editedEpochMonths.length ? { targetEpochMonths: editedEpochMonths } : {}),
      ts: Date.now(),
    }

    onWeeklyEdit(ew, patch)
  }


  const monthlyEditsByMonth = plannerSession?.monthlyEditsByMonth || {}
  const weeklyEditsByWeek = plannerSession?.weeklyEditsByWeek || {}
  const weekToMonthDayCounts = plannerSession?.weekToMonthDayCounts || new Map()

  const effectiveWeeklyEditsByWeek = useMemo(() => {
    if (!Object.keys(monthlyEditsByMonth || {}).length) return weeklyEditsByWeek
    return rollupMonthlyToWeekly({
      weeklyBase: { editsByWeek: { ...(weeklyEditsByWeek || {}) } },
      monthlyBase: (plannerSession?.monthlyBaseUi || plannerSession?.monthlyBase || []),
      monthlyEdits: monthlyEditsByMonth,
      weekToMonthDayCounts,
      calcMode: plannerSession?.calcMode || calcMode || 'allocate',
      weeklyActualSalesByWeek: plannerSession?.weeklyActualSalesByWeek || {},
    })
  }, [monthlyEditsByMonth, weeklyEditsByWeek, weekToMonthDayCounts, plannerSession?.monthlyBaseUi, plannerSession?.monthlyBase, plannerSession?.weeklyActualSalesByWeek, plannerSession?.calcMode, calcMode])

  // When monthly edits roll down into weeks, reflect them into the visible weekly draft fields.
  useEffect(() => {
    if (!apiRow) return
    const epochWeeks = getDisplayEpochWeeks(apiRow)
    if (!epochWeeks.length) return

    setDraft((prev) => {
      const next = { ...prev }
      const planPercent = [...(next.planPercent || [])]
      const planSS = [...(next.planSS || [])]
      const mdPlanPercent = [...(next.mdPlanPercent || [])]
      const invPlan = [...(next.invPlan || [])]

      epochWeeks.forEach((ew, idx) => {
        const e = effectiveWeeklyEditsByWeek?.[String(ew)]
        if (!e) return
        if (typeof e.planPercent !== 'undefined') planPercent[idx] = Number(e.planPercent)
        if (typeof e.planSS !== 'undefined') planSS[idx] = Number(e.planSS)
        if (typeof e.markdownPlanPercent !== 'undefined') mdPlanPercent[idx] = Number(e.markdownPlanPercent)
        if (typeof e.planInvBOM !== 'undefined') invPlan[idx] = Number(e.planInvBOM)
      })

      next.planPercent = planPercent
      next.planSS = planSS
      next.mdPlanPercent = mdPlanPercent
      next.invPlan = invPlan
      return next
    })
  }, [apiRow, effectiveWeeklyEditsByWeek])

  const weeklyChart = useMemo(() => {
    if (!periodData?.length) return { labels: [], chartData: new Map() }
    const labels = periodData.map((p) => p?.headerLabel || p?.shortPeriod || p?.period)
    const m = new Map()
    m.set('SalesPlan', periodData.map((p) => Number(p?.salesPlan || 0)))
    m.set('InventoryBomPlan', periodData.map((p) => Number(p?.planInvBOM || 0)))
    m.set('MarkdownsPlan', periodData.map((p) => Number(p?.markdownPlan || 0)))
    m.set('InflowPlanRetail', periodData.map((p) => Number(p?.planInflowRetail || 0)))
    return { labels, chartData: m }
  }, [periodData])

  useEffect(() => {
    if (typeof onChartData === 'function') onChartData(weeklyChart.chartData)
    if (typeof onChartLabels === 'function') onChartLabels(weeklyChart.labels)
    if (typeof onPeriodData === 'function') onPeriodData(periodData)
  }, [weeklyChart, periodData, onChartData, onChartLabels, onPeriodData])

  // Reset seed flag whenever the API row changes (new location/class/plan loaded)
  // so that seeding can fire once the monthly data arrives.
  useEffect(() => {
    hasAutoSeededRef.current = false
  }, [clientId, locationClassId, weeklyPlanSettingsId])

  // --- Reactive cascade: seed weekly from monthly base once both are available.
  // This effect depends on BOTH apiRow AND plannerSession so it re-runs when monthly loads.
  useEffect(() => {
    if (!apiRow) return
    if (dirty) return
    if (hasAutoSeededRef.current) return

    // Use plannerSession directly (from props) - never the ref, which may be stale
    const liveMonthlyBaseUi = plannerSession?.monthlyBaseUi
    const hasMonthBase = Array.isArray(liveMonthlyBaseUi) && liveMonthlyBaseUi.length > 0

    // Monthly data not yet loaded - wait for next render when it arrives
    if (!hasMonthBase) return

    // If the API already has non-zero weekly plan dollars, don't override
    const weeklySalesPlan = csvToNums(apiRow?.WeeklySalesPlanL12W)
    const weeklyMdPlan = csvToNums(apiRow?.WeeklyMarkdownsPlanL12W)
    const allZero = (arr) => Array.isArray(arr) && arr.length > 0 && arr.every((v) => Number(v || 0) === 0)
    const shouldCascade = (!weeklySalesPlan.length || allZero(weeklySalesPlan)) &&
                          (!weeklyMdPlan.length || allZero(weeklyMdPlan))
    if (!shouldCascade) return

    const epochWeeks = getDisplayEpochWeeks(apiRow)
    if (!epochWeeks.length) return

    const weeklyInvActual = csvToNums(apiRow?.InventoryL12W)

    // Pass liveMonthlyBaseUi, weekToMonthDayCounts, and weeklyActualSalesByWeek explicitly
    // so we never read stale values from the ref.
    const liveW2M = plannerSession?.weekToMonthDayCounts
    const liveActuals = plannerSession?.weeklyActualSalesByWeek
    const seeded = computeSeedFromMonthlyBase({
      epochWeeks,
      weeklyInvActual,
      monthlyBaseUi: liveMonthlyBaseUi,
      weekToMonthDayCounts: liveW2M,
      weeklyActualSalesByWeek: liveActuals,
    })

    const hasSeededValues = seeded.seedSales.some((v) => Number(v || 0) > 0)

    // Only mark as seeded if we actually got values - otherwise retry next render
    if (!hasSeededValues) return

    hasAutoSeededRef.current = true
    setDraft((prev) => ({
      ...prev,
      salesPlan: seeded.seedSales,
      mdPlan: seeded.seedMd,
      invPlan: seeded.seedInv,
      planPercent: seeded.seedPlanPct,
      mdPlanPercent: seeded.seedMdPct,
    }))
    setSeedSource('monthly')
  }, [apiRow, plannerSession, dirty])

  const effectiveSourceLabel = useMemo(() => {
    const hasMonthlyOverrides = Object.keys(plannerSession?.monthlyEditsByMonth || {}).length > 0
    const hasWeeklyOverrides = Object.keys(plannerSession?.weeklyEditsByWeek || {}).length > 0
    const hasUnsavedDailyDraft = !!(plannerSession?.dailyByDate && plannerSession?.dailyIndex?.byDate && plannerSession.dailyByDate !== plannerSession.dailyIndex.byDate)

    if (hasMonthlyOverrides && hasUnsavedDailyDraft && !hasWeeklyOverrides) {
      return 'monthly'
    }

    if (hasWeeklyOverrides) {
      return 'weekly'
    }

    return seedSource
  }, [plannerSession, seedSource])

  if (!weeklyPlanSettingsId || !monthlyPlanSettingsId) {
    return (
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle1">Weekly Plan</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Waiting for both weekly and monthly planner identifiers for this location class. The weekly planner context needs each table's matching <code>plan_settings_id</code>.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ border: '1px solid #e7e7eb', borderRadius: 1, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1">Weekly Plan</Typography>
          {loading ? (
            <Chip size="small" label="Loading…" variant="outlined" />
          ) : dirty ? (
            <Chip size="small" label="Unsaved changes" color="warning" />
          ) : (
            <Chip size="small" label="Loaded" variant="outlined" />
          )}

          {effectiveSourceLabel === 'monthly' ? (
            <Chip size="small" label="Loaded from Monthly" color="info" variant="outlined" />
          ) : effectiveSourceLabel === 'weekly' ? (
            <Chip size="small" label="Loaded from Weekly" color="default" variant="outlined" />
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Save/Reset controls live in the global bottom action bar now */}
          <Button size="small" variant="outlined" onClick={resetDraft} disabled={!dirty}>
            Reset draft
          </Button>
        </Box>
      </Box>

      <PlanEditMonthlyHeader
        annualValues={headerAnnualValues}
        collapsed={annualHeaderCollapsed}
        onCollapsedChange={onAnnualHeaderCollapsedChange}
        onApplyAnnualSales={null}
        onApplyAnnualMarkdowns={null}
        onApplyAnnualTurn={null}
        onApplyAnnualImu={null}
        onBalanceAllInflows={null}
        onSimpleKFlow={null}
        readOnly={true}
      />

      <WeeklyPlanningGrid
        data={periodData}
        summary={summaryData}
        onChange={handleCellChange}
        onCopyActuals={handleCopyActualsToPlan}
      />

      {debugInfo ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Debug weekly context: hasRow=<code>{String(debugInfo.hasRow)}</code>, keys=<code>{debugInfo.keys.join(', ')}</code>, weekly_plan_version=<code>{debugInfo.weeklyPlanVersion ?? 'n/a'}</code>
        </Typography>
      ) : null}
    </Box>
  )
}

export default PlanEditWeeklyTableShell
